import clsx from 'clsx';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnInstance, Row, useTable } from 'react-table';
import { KTCardBody, KTIcon } from '../../../../../../_metronic/helpers';
import { useGetPosts } from '../api/posts.queries';
import { PostDto, PostModalState } from '../model';
import { PostCustomRow } from './PostCustomRow';
import { PostPublishButton } from './PostPublishButton';
import { postsColumns } from './_columns';

const LANGUAGES = ['ru', 'en', 'hi', 'id', 'zh'] as const;
type Lang = typeof LANGUAGES[number];

interface Props {
	openModal: PostModalState | null;
	setOpenModal: (open: PostModalState | null) => void;
}

export const FeedPostsTable: FC<Props> = ({ openModal, setOpenModal }) => {
	const navigate = useNavigate();
	const { data: posts, isLoading } = useGetPosts();
	const [activeLang, setActiveLang] = useState<Lang>('ru');

	const data = useMemo(() => posts || [], [posts]);

	const columns = useMemo(
		() => [
			...postsColumns(activeLang),
			{
				id: 'actions',
				Cell: ({ row }: { row: Row<PostDto> }) => (
					<div className='d-flex gap-4 justify-content-end'>
						<PostPublishButton post={row.original} />
						<button
							className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
							onClick={() =>
								navigate(`/apps/feed/posts/edit/${row.original.uuid}`)
							}
						>
							<KTIcon iconName='pencil' className='fs-3' />
						</button>
						<button
							className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
							onClick={() =>
								setOpenModal({ type: 'delete', data: row.original })
							}
						>
							<KTIcon iconName='trash' className='fs-3' />
						</button>
					</div>
				),
			},
		],
		[activeLang, setOpenModal, navigate]
	);

	const { getTableProps, getTableBodyProps, headers, rows, prepareRow } =
		useTable({
			columns,
			data,
		} as any);

	return (
		<KTCardBody className='py-4'>
			<div className='d-flex align-items-center justify-content-between'>
				<ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0'>
					{LANGUAGES.map(lang => (
						<li key={lang} className='nav-item'>
							<button
								type='button'
								className={clsx('nav-link text-uppercase fw-bold', {
									active: activeLang === lang,
								})}
								onClick={() => setActiveLang(lang)}
							>
								{lang}
							</button>
						</li>
					))}
				</ul>

				<button
					type='button'
					className='btn btn-primary btn-sm'
					disabled={isLoading}
					onClick={() => navigate('/apps/feed/posts/create')}
				>
					{isLoading ? (
						<div
							className='spinner-border spinner-border-sm me-2'
							role='status'
						></div>
					) : (
						<KTIcon iconName='plus' className='fs-2 ps-1 me-2' />
					)}
					Добавить
				</button>
			</div>
			<div
				className='table-responsive w-100 pb-8'
				style={{ transform: 'scaleY(-1)' }}
			>
				<table
					style={{ transform: 'scaleY(-1)' }}
					id='kt_table_tags'
					className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
					{...getTableProps()}
				>
					<thead>
						<tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
							{headers?.map((column: ColumnInstance<any>) => (
								<th {...column.getHeaderProps()} key={column.id}>
									{column.render('Header')}
								</th>
							))}
						</tr>
					</thead>
					<tbody className='text-gray-600 fw-bold' {...getTableBodyProps()}>
						{rows?.length > 0 ? (
							rows.map((row: Row<any>, i) => {
								prepareRow(row);
								return <PostCustomRow row={row} key={`row-${i}-${row.id}`} />;
							})
						) : (
							<tr>
								<td colSpan={7}>
									<div className='d-flex text-center w-100 align-content-center justify-content-center'>
										No matching records found
									</div>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			{/* {isLoading && <UsersListLoading />} */}
		</KTCardBody>
	);
};
